// src/services/authService.ts
import axios from 'axios';
import { getBasename } from './api';
import type { User as UserType, UserRole, UserStatus } from '../types/user.types';

// Re-export User type for consumers
export type User = {
  id: number;
  username: string;
  email: string;
  name: string;
  role?: UserRole | string;
  status?: UserStatus | string;
  instituicoes?: string[];
  secretaria?: string;
  documento?: string;
  parlamentarId?: string | null;
  receberEmailsNotificacao?: boolean;
  /** Gestor da Parceria — atribuicao booleana, so valida quando role = SECRETARIA */
  gestorParceria?: boolean;
  /** Operador de secretaria — OPERADOR que atua em nome de uma secretaria (nome em `secretaria`) */
  operadorSecretaria?: boolean;
};

interface LoginResponse {
  success: boolean;
  token: string;
  refreshToken?: string;
  user: User;
}

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

export const login = async (credentials: { document: string; password: string; hCaptchaToken?: string }): Promise<LoginResponse> => {
  try {
    const payload = {
      username: credentials.document,
      password: credentials.password,
      hCaptchaToken: credentials.hCaptchaToken,
    };
    const { data } = await axios.post<LoginResponse>('/api/auth/login', payload);
    if (data.refreshToken) {
      sessionStorage.setItem('refreshToken', data.refreshToken);
    }
    return data;
  } catch (err) {
    console.error('authService.login error:', err);
    throw err;
  }
};

export const logout = async (): Promise<{ success: boolean }> => {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('user');
  return { success: true };
};

/**
 * Attempts to get a new access token using the stored refresh token.
 * Uses a plain axios instance (not the intercepted `api`) to avoid infinite loops.
 * Returns the new access token on success, or null if the refresh token is missing/expired.
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = sessionStorage.getItem('refreshToken');
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post<RefreshResponse>('/api/auth/refresh', { refreshToken });
    sessionStorage.setItem('token', data.accessToken);
    sessionStorage.setItem('refreshToken', data.refreshToken);
    return data.accessToken;
  } catch {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
    return null;
  }
};

/**
 * Decodes the JWT payload without verifying the signature.
 * Used client-side only to read exp/iat claims.
 */
export const decodeJwtPayload = (token: string): { exp?: number; [key: string]: any } | null => {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const getCurrentUser = (): User | null => {
  const userStr = sessionStorage.getItem('user');
  if (!userStr) return null;
  try {
    const parsed = JSON.parse(userStr);
    if (parsed && typeof parsed === 'object') {
      return parsed as User;
    }
    return null;
  } catch (e) {
    console.warn('authService.getCurrentUser: failed to parse stored user, clearing invalid value', userStr, e);
    sessionStorage.removeItem('user');
    return null;
  }
};
